import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AvatarWithName from '@components/avatar-with-name/AvatarWithName';
import { getGroupCardColor } from '@components/group-card/utils';
import { Button, Icon, Modal } from '@components/index';
import { AccessMode, AccountType } from '@constants/generic';
import { LEAD_MANAGEMENT_ROLES } from '@constants/role';
import { DASHBOARD, LEAD_MANAGEMENT } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import { RoleType } from '@features/team-management/constants';
import { updateBranchFilter } from '@layouts/main-header/branchSlice';
import {
  setAccountType,
  setBlacklistedModal,
  setCurrentOrg
} from '@layouts/main-header/userSlice';
import {
  getLocalStorageItem,
  setMultipleLocalStorageItems
} from '@utils/storage';
import { Org } from 'types/organization';

interface SwitchAccountModalProps {
  showSwitchAccountModal: boolean;
  onCancelModal: () => void;
  orgList?: Org[];
}

const SwitchAccountModal = ({
  showSwitchAccountModal,
  onCancelModal,
  orgList
}: SwitchAccountModalProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentOrgId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(currentOrgId);

  const onSwitchOrg = () => {
    const selectedOrg = orgList?.find((org) => org.id === selectedOrgId);
    if (selectedOrg) {
      const {
        id,
        default_branch_id: defaultBranchId,
        default_branch_name: defaultBranchName,
        role
      } = selectedOrg;
      setMultipleLocalStorageItems([
        { key: STORAGE_KEYS.ORGANIZATION_ID, value: id },
        { key: STORAGE_KEYS.BRANCH_ID, value: defaultBranchId },
        { key: STORAGE_KEYS.BRANCH_NAME, value: defaultBranchName }
      ]);
      dispatch(
        updateBranchFilter({
          branchId: defaultBranchId,
          branchName: defaultBranchName
        })
      );
      // Update the active org in the store synchronously so dashboard widgets
      // that derive their org id from `currentOrg` immediately use the new org.
      // Otherwise they fire requests against the previous (blacklisted) org,
      // which 403s and forces a logout.
      dispatch(setCurrentOrg(selectedOrg));
      dispatch(setAccountType({ type: selectedOrg.type }));
      onCancelModal();

      // Surface the blacklisted modal right away instead of routing into the app.
      if (selectedOrg.access_mode === AccessMode.BLACKLISTED) {
        dispatch(setBlacklistedModal({ orgList }));
        return;
      }
      if (LEAD_MANAGEMENT_ROLES.includes(role as unknown as RoleType)) {
        navigate(LEAD_MANAGEMENT.LEAD_DASHBOARD);
      } else {
        navigate(DASHBOARD);
      }
      sendMixpanelEvent(Event.SWITCH_ACCOUNT_CONTINUE_BUTTON_CLICKED, {
        switched_to: selectedOrg.name
      });
    }
  };

  return (
    <Modal
      isOpen={showSwitchAccountModal}
      onCancel={onCancelModal}
      className="max-h-90.5 w-120 !bg-cofeeNeutral1"
    >
      <div className="flex w-full items-center justify-between border-b border-cofeeNeutral5 p-4">
        <div className="text-xl font-semibold text-cofeeNeutral10">
          Switch Account
        </div>
        <Icon
          name="close"
          size="24"
          onClick={onCancelModal}
          className="cursor-pointer"
        />
      </div>
      <div className="custom-scroll flex max-h-56 flex-col gap-1 overflow-auto px-4 py-2">
        {orgList?.map((org) => (
          <div
            key={org.id}
            className="flex min-h-[80px] cursor-pointer items-center justify-between rounded-lg p-2 hover:bg-cofeeGreyBlue75"
            onClick={() => setSelectedOrgId(org.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setSelectedOrgId(org.id);
              }
            }}
            tabIndex={0}
            role="button"
          >
            <AvatarWithName
              name={
                org.type === AccountType.INDIVIDUAL
                  ? org.display_name || ''
                  : org.name || ''
              }
              subText={org.role_name || ''}
              imageUrl={org.image_url}
              textWrapperClass="!items-start !text-left"
              wrapperClass="mr-4"
              nameClass="break-all"
              avatarClass="!flex-shrink-0"
              bgColor={getGroupCardColor(org.id)}
            />
            <div className="flex w-6 shrink-0 items-center justify-center">
              {org.id === selectedOrgId ? (
                <Icon
                  name="rounded-tick"
                  className="size-5 !min-w-5 shrink-0"
                />
              ) : (
                <div className="size-5 min-w-5 shrink-0 rounded-full border border-cofeeNeutral6" />
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-4 border-t border-cofeeNeutral5 px-6 py-4">
        <Button
          variant="secondary"
          label="Cancel"
          handleClick={onCancelModal}
          className="h-11 min-w-25 !border-cofeeNeutral5 !bg-white"
          labelStyle="!text-cofeeNeutral10"
        />
        <Button
          variant="primary"
          label="Continue"
          handleClick={onSwitchOrg}
          className={`h-11 min-w-25 ${
            selectedOrgId === currentOrgId ? '' : '!bg-cofeePrimary6'
          }`}
          labelStyle="!text-cofeeNeutral1"
          disabled={selectedOrgId === currentOrgId}
        />
      </div>
    </Modal>
  );
};

export default SwitchAccountModal;
